import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';

function ProductOptionEditor({ groupId, onClose }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  const [optionFormData, setOptionFormData] = useState({
    name: '',
    description: '',
    price: 0,
    available: true
  });

  const loadOptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/product-options/options/group/${groupId}`);
      setOptions(response.data.map((option) => ({
        ...option,
        price: Number(option.price || option.additional_price || 0),
        available: option.available !== false
      })));
    } catch (error) {
      toast.error('Erro ao carregar opções');
      console.error('Erro ao carregar opções:', error);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      loadOptions();
    }
  }, [groupId, loadOptions]);

  const handleOpenOptionDialog = (option = null) => {
    setEditingOption(option);
    if (option) {
      setOptionFormData({
        name: option.name,
        description: option.description,
        price: option.price || option.additional_price || 0,
        available: option.available
      });
    } else {
      setOptionFormData({
        name: '',
        description: '',
        price: 0,
        available: true
      });
    }
    setOptionDialogOpen(true);
  };

  const handleCloseOptionDialog = () => {
    setOptionDialogOpen(false);
    setEditingOption(null);
  };

  const handleOptionInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOptionFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveOption = async (e) => {
    e.preventDefault();
    
    try {
      const requestData = {
        ...optionFormData,
        group_id: groupId
      };
      
      if (editingOption) {
        await api.put(`/product-options/options/${editingOption.id}`, optionFormData);
        toast.success('Opção atualizada com sucesso!');
      } else {
        try {
          await api.post('/product-options/options', requestData, {
            headers: {
              'Content-Type': 'application/json'
            }
          });
          toast.success('Opção criada com sucesso!');
        } catch (error) {
          throw error;
        }
      }
      
      handleCloseOptionDialog();
      loadOptions();
    } catch {
      toast.error('Erro ao salvar opção');
    }
  };

  const handleDeleteOption = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar esta opção?')) {
      try {
        await api.delete(`/product-options/options/${id}`);
        toast.success('Opção deletada com sucesso!');
        loadOptions();
      } catch {
        toast.error('Erro ao deletar opção');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          Opções do Grupo
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenOptionDialog()}
        >
          Nova Opção
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Preço Adicional</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.map((option) => (
              <TableRow key={option.id}>
                <TableCell>{option.name}</TableCell>
                <TableCell>{option.description || '-'}</TableCell>
                <TableCell>
                  {Number(option.price || 0) > 0 ? (
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      +R$ {Number(option.price || 0).toFixed(2)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Incluído
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={option.available ? 'Ativo' : 'Inativo'} 
                    size="small" 
                    color={option.available ? 'success' : 'default'} 
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenOptionDialog(option)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteOption(option.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {options.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="textSecondary">
            Nenhuma opção encontrada. Adicione opções para este grupo!
          </Typography>
        </Box>
      )}

      {/* Dialog de Opção */}
      <Dialog open={optionDialogOpen} onClose={handleCloseOptionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOption ? 'Editar Opção' : 'Nova Opção'}
        </DialogTitle>
        <form onSubmit={handleSaveOption}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="name"
                label="Nome da Opção"
                value={optionFormData.name}
                onChange={handleOptionInputChange}
                fullWidth
                required
              />
              
              <TextField
                name="description"
                label="Descrição"
                value={optionFormData.description}
                onChange={handleOptionInputChange}
                fullWidth
                multiline
                rows={3}
              />
              
              <TextField
                name="price"
                label="Preço Adicional"
                type="number"
                value={optionFormData.price}
                onChange={handleOptionInputChange}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    name="available"
                    checked={optionFormData.available}
                    onChange={handleOptionInputChange}
                    color="primary"
                  />
                }
                label="Disponível"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOptionDialog}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained">
              {editingOption ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default ProductOptionEditor;
